'use client';

import { authTokenLocalStorageKey } from '@/config';
import { User } from 'lucia';
import { createContext, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

type IAuthContext = {
  isAuthenticated?: boolean;
  user?: User;
  token?: string;
  setAuth: (user: User, token: string) => void;
  removeAuth: () => void;
  setUser: (user: User) => void;
};

export const AuthContext = createContext<IAuthContext | undefined>(undefined);

type Props = {
  children: React.ReactNode;
};

export default function AuthProvider({ children }: Props) {
  const [userState, setUserState] = useLocalStorage<User | undefined>(
    'user_auth_data',
    undefined,
  );
  const [token, setToken] = useLocalStorage<string>(
    authTokenLocalStorageKey ?? '',
    '',
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (userState) {
      setIsLoading(false);
    }
  }, [userState]);

  useEffect(() => {
    setIsAuthenticated(!!userState);
  }, [userState]);

  const setAuth = (user: User, token: string) => {
    setUserState(user);
    setToken(token);
    setIsAuthenticated(true);
  };

  const removeAuth = () => {
    setUserState(undefined);
    setToken('');
    setIsAuthenticated(false);
  };

  const setUser = (user: User) => {
    setUserState(user);
  };

  return (
    <AuthContext.Provider
      value={{
        user: userState,
        token,
        isAuthenticated: isLoading ? undefined : isAuthenticated,
        setAuth,
        removeAuth,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
